"use client"

export const dynamic = 'force-dynamic'

import { Button } from "@/components/ui/button"
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { ArrowRight, Globe, Zap, Shield, Code, Upload, Download, DollarSign, Users, Key, Infinity, TrendingDown } from "lucide-react"
import Link from "next/link"
import { Navigation } from "@/components/navigation"
import { Footer } from "@/components/footer"
import { useState } from "react"

export default function HomePage() {
  const [selectedPlatform, setSelectedPlatform] = useState<'lokalise' | 'crowdin'>('crowdin')

  return (
    <div className="min-h-screen">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-20 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-muted text-muted-foreground text-sm font-medium mb-6">
              <Infinity className="h-4 w-4 mr-2" />
              Localization Without Limits, AI Without Markup
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              Unlimited AI Translation for{" "}
              <span className="text-primary">Pennies per Word</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Enterprise localization platforms charge $200-500/month. Get the same AI-powered translation features for $22-30/month using your own OpenAI key.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button size="lg" asChild>
                <Link href="/auth/signup">
                  Start Free - No Credit Card
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link href="/calculator">See How Much You Save</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Why pay 5-10x more for the same AI translation? See how we compare to enterprise platforms →
            </p>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-4">Why pay 5-10x more for the same AI translation?</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Traditional localization platforms charge massive markups on AI translation. We let you use your own AI key and pay the actual cost.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <Card className="border-l-4 border-l-green-500 hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-green-500/20 hover:border-green-400 dark:hover:border-green-600">
            <CardHeader>
              <Key className="h-8 w-8 text-green-600 mb-2" />
              <CardTitle>Bring Your Own AI</CardTitle>
              <CardDescription>
                Use your own OpenAI key and pay exactly what OpenAI charges - typically $0.001 per 1K tokens, not markup fees that competitors charge.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="border-l-4 border-l-blue-500 hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-blue-500/20 hover:border-blue-400 dark:hover:border-blue-600">
            <CardHeader>
              <Infinity className="h-8 w-8 text-blue-600 mb-2" />
              <CardTitle>Truly Unlimited Everything</CardTitle>
              <CardDescription>
                Unlimited languages, projects, files, team members, and translations. No per-seat licensing, no usage caps, no hidden fees.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="border-l-4 border-l-purple-500 hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-purple-500/20 hover:border-purple-400 dark:hover:border-purple-600">
            <CardHeader>
              <DollarSign className="h-8 w-8 text-purple-600 mb-2" />
              <CardTitle>5-10x Cheaper</CardTitle>
              <CardDescription>
                While enterprise platforms charge hundreds monthly plus per-user fees, you pay only our low platform fee plus your actual AI costs.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="border-l-4 border-l-orange-500 hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-orange-500/20 hover:border-orange-400 dark:hover:border-orange-600">
            <CardHeader>
              <Shield className="h-8 w-8 text-orange-600 mb-2" />
              <CardTitle>Complete Transparency</CardTitle>
              <CardDescription>
                Track every API call and cost in real-time. No mysterious charges, no markup, no vendor lock-in.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="border-l-4 border-l-red-500 hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-red-500/20 hover:border-red-400 dark:hover:border-red-600">
            <CardHeader>
              <Zap className="h-8 w-8 text-red-600 mb-2" />
              <CardTitle>Developer-Friendly Scale</CardTitle>
              <CardDescription>
                Built for developers by developers. Full REST API, CLI tools, and integrations that work how you work.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="border-l-4 border-l-indigo-500 hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-indigo-500/20 hover:border-indigo-400 dark:hover:border-indigo-600">
            <CardHeader>
              <Users className="h-8 w-8 text-indigo-600 mb-2" />
              <CardTitle>Team Collaboration</CardTitle>
              <CardDescription>
                Unlimited team members can review, edit, and approve translations with powerful collaboration tools.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </section>

      {/* Cost Comparison */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-4">
                Real-World Cost Comparison
              </h2>
              <p className="text-muted-foreground mb-6">
                What moderate localization usage actually costs per month
              </p>
              
              {/* Platform Toggle */}
              <div className="inline-flex items-center rounded-lg bg-muted p-1 mb-8">
                <button
                  onClick={() => setSelectedPlatform('lokalise')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    selectedPlatform === 'lokalise'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  vs Lokalise
                </button>
                <button
                  onClick={() => setSelectedPlatform('crowdin')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    selectedPlatform === 'crowdin'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  vs Crowdin
                </button>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="p-6">
                <div className="flex items-center mb-4">
                  <div className="w-3 h-3 bg-orange-500 rounded-full mr-3"></div>
                  <h3 className="text-xl font-semibold">
                    {selectedPlatform === 'lokalise' ? 'Lokalise' : 'Crowdin'}
                  </h3>
                </div>
                
                {selectedPlatform === 'lokalise' ? (
                  <>
                    <div className="text-3xl font-bold text-orange-600 dark:text-orange-400 mb-2">$210-405</div>
                    <p className="text-muted-foreground mb-4">per month (15-person team)</p>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span>Start plan (10 users)</span>
                        <span className="font-medium">$140/month</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Additional 5 users</span>
                        <span className="font-medium">$70/month</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Key limitations</span>
                        <span className="font-medium">5K keys</span>
                      </div>
                      <div className="border-t pt-3">
                        <div className="flex justify-between font-bold">
                          <span>Total (Start plan)</span>
                          <span className="text-orange-600 dark:text-orange-400">$210/month</span>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground mt-2">
                        Essential plan would be $405/month for same team
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-3xl font-bold text-orange-600 dark:text-orange-400 mb-2">$250-350</div>
                    <p className="text-muted-foreground mb-4">per month (15-person team, estimated)</p>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span>Base subscription</span>
                        <span className="font-medium">$150-200/month</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Additional users</span>
                        <span className="font-medium">$100-150/month</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Word limitations</span>
                        <span className="font-medium">Per plan tier</span>
                      </div>
                      <div className="border-t pt-3">
                        <div className="flex justify-between font-bold">
                          <span>Total (Team plan)</span>
                          <span className="text-orange-600 dark:text-orange-400">$250/month</span>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground mt-2">
                        Premium plan would be $350/month for same team
                      </div>
                    </div>
                  </>
                )}
              </Card>
              
              <Card className="p-6 border-2 border-primary">
                <div className="flex items-center mb-4">
                  <div className="w-3 h-3 bg-primary rounded-full mr-3"></div>
                  <h3 className="text-xl font-semibold">Locsy</h3>
                </div>
                <div className="text-3xl font-bold text-primary mb-2">$22-30</div>
                <p className="text-muted-foreground mb-4">total monthly cost</p>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span>Platform fee</span>
                    <span className="font-medium">$20/month</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Your OpenAI costs</span>
                    <span className="font-medium">$2-10/month</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Team members</span>
                    <span className="font-medium text-primary">Unlimited</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Projects & languages</span>
                    <span className="font-medium text-primary">Unlimited</span>
                  </div>
                  <div className="border-t pt-3">
                    <div className="flex justify-between font-bold">
                      <span>Total</span>
                      <span className="text-primary">$22-30/month</span>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">
                    Zero markup on AI costs. Estimates based on GPT-4.1-mini pricing for guidance only.
                  </div>
                </div>
              </Card>
            </div>
            
            <div className="mt-8 text-center">
              <Card className="p-6 border-2 border-primary">
                <div className="flex items-center justify-center mb-2">
                  <TrendingDown className="h-5 w-5 text-primary mr-2" />
                  <span className="font-semibold text-lg">
                    Save {selectedPlatform === 'lokalise' ? '85-95%' : '85-92%'} on localization costs
                  </span>
                </div>
                <p className="text-muted-foreground text-sm">
                  Monthly savings: {selectedPlatform === 'lokalise' ? '$180-383' : '$220-328'} • 
                  Annual savings: {selectedPlatform === 'lokalise' ? '$2,160-4,596' : '$2,640-3,936'}
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-4">How it works</h2>
          <p className="text-muted-foreground">From project setup to global deployment in three simple steps</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <Card className="border-l-4 border-l-blue-500 hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-blue-500/20 hover:border-blue-400 dark:hover:border-blue-600">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-sm font-bold">
                  1
                </div>
              </div>
              <CardTitle>Create Project & Enable Languages</CardTitle>
              <CardDescription>
                Set up your localization project and select target languages for translation
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="border-l-4 border-l-green-500 hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-green-500/20 hover:border-green-400 dark:hover:border-green-600">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Upload className="h-8 w-8 text-primary" />
              </div>
              <CardTitle>Upload Source Files</CardTitle>
              <CardDescription>
                Upload your base language files (JSON, YAML, etc.) containing all your app's text
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="border-l-4 border-l-purple-500 hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-purple-500/20 hover:border-purple-400 dark:hover:border-purple-600">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Download className="h-8 w-8 text-primary" />
              </div>
              <CardTitle>Download & Sync</CardTitle>
              <CardDescription>
                Download translated files or integrate with your CI/CD pipeline for automated deployment
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-20 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold mb-4">Ready to go global?</h2>
          <p className="text-muted-foreground mb-8">
            Join thousands of developers who trust our AI-powered localization service
          </p>
          <Button size="lg" asChild>
            <Link href="/auth/signup">
              Start Translating Now
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  )
}
